import React, { useState } from 'react';
import { Dialog } from '@material-ui/core';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { MockApolloClient } from 'mock-apollo-client';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ClientSummaryNode, CoachingVisitState } from '../../../../generated/webapp_gql';
import { getCurrentUser } from '../../../../selectors/userSelectors';
import { ReactComponent as IconClose } from '../../../../assets/svgs/icon-close.svg';
import PaymentReminder from '../PaymentReminder/PaymentReminder';
import ScheduleSessionConfirmation from '../ScheduleSessionConfirmation';
import ScheduleSessionForm from '../ScheduleSessionForm/ScheduleSessionForm';
import { SessionDetails } from '../ScheduleSessionForm/constants';
import { ScheduleSessionModalTestID } from './constants';
import { useCancelEventMutation } from '../../SessionsList/SessionsByDay/SessionRow/documents/CancelEventMutation.generated';
import ExtendTherapyReminder from '../ExtendTherapyReminder/ExtendTherapyReminder';

interface Props {
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onClose: () => void;
}

const ScheduleSessionModal: React.FC<Props> = ({ memberClient, onClose }) => {
  const [sessionDetails, setSessionDetails] = useState<SessionDetails>();
  const [hasSentReminderSuccessfully, setHasSentReminderSuccessfully] = useState<boolean>(false);
  const [showingExtendTherapyModalForMember, setShowingExtendTherapyModalForMember] = useState<
    ClientSummaryNode | undefined
  >();

  const snackbar = useSnackbar();
  const history = useHistory();
  const currentUser = useSelector(getCurrentUser);
  const { hpiRemovePendingInsuranceSessionState, showHpiClientStatusInfo } = useFlags();

  const handleSuccess = (newSessionDetails) => setSessionDetails(newSessionDetails);

  const [cancelEventMutation, { loading: isCanceling }] = useCancelEventMutation({
    client: memberClient,
    onCompleted: (data) => {
      if (data?.cancelEvent?.ok) {
        onClose();
      } else {
        snackbar.enqueueSnackbar('Could not cancel event', { variant: 'error' });
      }
    },
    onError: () => {
      snackbar.enqueueSnackbar('Could not cancel event', { variant: 'error' });
    },
  });

  const canShowReminderModal =
    showHpiClientStatusInfo &&
    currentUser.isTherapist &&
    sessionDetails?.member?.isCompanyHpiEnabled;

  const canShowPaymentReminder =
    canShowReminderModal &&
    (sessionDetails?.sessionState === CoachingVisitState.PendingPayment ||
      sessionDetails?.sessionState === CoachingVisitState.PendingConfirmation);

  const canShowConfirmationModal = !(canShowReminderModal && canShowPaymentReminder);

  const canShowExtendTherapyModal =
    hpiRemovePendingInsuranceSessionState && showingExtendTherapyModalForMember;

  const handleClose = () => {
    if (sessionDetails) {
      history.go(0);
    }

    onClose();
  };

  const handleCancel = () => {
    if (
      sessionDetails?.sessionState === CoachingVisitState.Scheduled ||
      hasSentReminderSuccessfully ||
      !canShowReminderModal
    ) {
      handleClose();

      return;
    }

    if (!(sessionDetails && canShowPaymentReminder)) return;

    cancelEventMutation({
      variables: {
        uuid: sessionDetails.sessionUuid,
        enqueueVisitCancelledEmailEvent: false,
      },
    });

    onClose();
  };

  const handleReminderSent = () => setHasSentReminderSuccessfully(true);

  return (
    <Dialog open maxWidth="md">
      <div style={{ width: 720 }} className="p-8 h-full relative">
        <button
          className="bg-gray-300 hover:bg-gray-200 cursor-pointer rounded-full h-10 w-10 absolute top-4 right-4 flex justify-center items-center"
          data-testid={ScheduleSessionModalTestID.CloseButton}
          onClick={handleCancel}
        >
          <IconClose className="w-3 h-3 text-gray-1000 fill-current stroke-current" />
        </button>

        <div
          data-testid={ScheduleSessionModalTestID.ScheduleSessionForm}
          className="flex flex-col justify-center items-center mt-4 mx-4"
        >
          {sessionDetails && canShowConfirmationModal && (
            <ScheduleSessionConfirmation sessionDetails={sessionDetails} />
          )}

          {!(sessionDetails || canShowExtendTherapyModal) && (
            <ScheduleSessionForm
              memberClient={memberClient}
              onCancel={onClose}
              onShowExtendTherapyCoverageModal={setShowingExtendTherapyModalForMember}
              onSuccess={handleSuccess}
            />
          )}

          {showingExtendTherapyModalForMember && canShowExtendTherapyModal && (
            <ExtendTherapyReminder
              member={showingExtendTherapyModalForMember}
              memberClient={memberClient}
              onCancel={() => setShowingExtendTherapyModalForMember(undefined)}
            />
          )}
          {sessionDetails && canShowPaymentReminder && (
            <PaymentReminder
              isCanceling={isCanceling}
              memberClient={memberClient}
              onCancel={handleCancel}
              onSuccess={handleReminderSent}
              sessionDetails={sessionDetails}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ScheduleSessionModal;
